import { authFetch } from '@/api/request';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAgentStore } from '@/store/agent-store';

export interface NodeOutput {
  status: 'pending' | 'running' | 'done' | 'failed';
  startedAt?: string;
  duration?: number;
  progress?: number;
  error?: string;
  mode?: string;
  gameplay?: string;
  skillName?: string;
  logs?: Array<{ ts: number; text: string; level?: 'info' | 'warn' | 'error' }>;
  artifacts?: Record<string, unknown>;
  images?: Array<{
    url: string;
    slot: string;
    prompt?: string;
    enhancedPrompt?: string;
    category?: string;
    transparent?: boolean;
  }>;
  scores?: Record<string, number>;
  verdict?: string;
  previewUrl?: string;
  downloadUrl?: string;
  fileSize?: number;
}

const STALE_TIMEOUT = 300_000;

function isPrePopulatedOnly(nodes: Record<string, NodeOutput>): boolean {
  const entries = Object.entries(nodes);
  if (entries.length === 0) return false;
  return entries.every(([key, n]) =>
    key === 'requirement' ? n.status === 'done' : n.status === 'pending',
  );
}

/**
 * 单一状态源归一化：旧会话曾把 prototype_qa 的浏览器/探针告警持久化为
 * failed，但只要有 htmlPath，素材与发布仍然可以继续。Workflow 页会聚合
 * 原型节点，主工作台会直接读取节点列表；在轮询入口统一修正，避免两边
 * 对同一会话显示不同阶段状态。
 */
function normalizeRecoverablePrototypeQa(nodes: Record<string, NodeOutput>): Record<string, NodeOutput> {
  const qa = nodes.prototype_qa;
  const htmlPath = (qa?.artifacts as Record<string, unknown> | undefined)?.htmlPath;
  if (qa?.status !== 'failed' || typeof htmlPath !== 'string' || !htmlPath) return nodes;
  return {
    ...nodes,
    prototype_qa: {
      ...qa,
      status: 'done',
      error: undefined,
      artifacts: { ...(qa.artifacts ?? {}), prototypeVerified: false, verificationWarning: true },
    },
  };
}

export interface PipelinePauseState {
  stage: string;
  next: string;
  status: 'paused' | 'done' | string;
  /** BUG-20260810-064: 用户主动终止标记——终止态可从断点继续 */
  terminatedByUser?: boolean;
  /** 节点主动请求确认时的暂停原因（例如视频确认节点）。 */
  pauseReason?: string;
  error?: string;
}

/**
 * 把后端轮询到的微调状态同步进 store（映射 preview→done 为终态）。
 * 仅在状态变化时写入，避免 2s 延迟的轮询覆盖 SSE 实时思考流。
 * 刷新后 SSE 断流，此函数是「微调完成不通知前端」的修复核心。
 */
function syncRefineState(refine: Record<string, unknown> | null | undefined): void {
  if (!refine || typeof refine !== 'object') return;
  const rawStatus = refine.status as string | undefined;
  if (!rawStatus) return;
  const nextStatus = rawStatus === 'preview' ? 'done' : rawStatus;
  const store = useAgentStore.getState();
  const cur = store.refineState;
  if (nextStatus === cur.status) return;
  store.setRefineState({
    status: nextStatus as 'running' | 'done' | 'failed' | 'idle',
    stage: refine.stage as 'understand' | 'apply' | 'verify' | undefined,
    baseVersion: refine.baseVersion as number | undefined,
    basePreviewUrl: refine.basePreviewUrl as string | undefined,
    targetVersion: refine.targetVersion as number | undefined,
    newPreviewUrl: refine.newPreviewUrl as string | undefined,
    playableId: refine.playableId as string | undefined,
    error: refine.error as string | undefined,
    nodeOutputs: (refine.nodeOutputs as Record<string, NodeOutput> | undefined) ?? cur.nodeOutputs,
  });
  // DO-NOT-REVERT: BUG-20260909-PW-003 后端已终态（done）且带新预览 URL 时主动请求预览切换——刷新/断流
  // 场景 SSE done 事件丢失，仅同步状态不切预览会让微调完成后预览滞留旧版本/黑屏（与 use-agent-chat
  // finally 核对、ProjectDetail HEAD 校验同属 PW-003 预览链路）
  if (nextStatus === 'done') {
    const newUrl = typeof refine.newPreviewUrl === 'string' ? refine.newPreviewUrl : undefined;
    if (newUrl && newUrl !== cur.newPreviewUrl) store.requestPreview(newUrl);
  }
}

export function usePipelinePoll(sessionId: string | null) {
  const [nodes, setNodes] = useState<Record<string, NodeOutput>>({});
  // nodes 归属的会话 ID：会话切换时 nodes 滞后一帧，消费方需以此做会话守卫，
  // 防止旧会话 nodes（如 buildNode.previewUrl）回填到新会话（BUG-20260821-001）
  const [nodesSessionId, setNodesSessionId] = useState<string | null>(null);
  const [pipeline, setPipeline] = useState<PipelinePauseState | null>(null);
  const [loading, setLoading] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval>>();
  const staleTimer = useRef<ReturnType<typeof setInterval>>();
  const lastChangeRef = useRef<number>(Date.now());
  const lastSnapshotRef = useRef<string>('');
  const prevSessionRef = useRef<string | null>(null);
  const pendingFailureRef = useRef<Record<string, number>>({});
  const nodesRef = useRef<Record<string, NodeOutput>>({});

  const nodeValues = Object.values(nodes);
  const allDone = nodeValues.length > 0 && nodeValues.every((n) => n.status === 'done' || n.status === 'failed');
  const hasRealActivity = Object.entries(nodes).some(([key, n]) =>
    key !== 'requirement' && (n.status === 'running' || n.status === 'done' || n.status === 'failed'),
  );
  const isRunning = nodeValues.some((n) => n.status === 'running');
  const prePopulatedOnly = isPrePopulatedOnly(nodes);
  const shouldPoll = hasRealActivity && !allDone;

  // 拉取节点 + 自动开启/关闭轮询
  const fetchNodes = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await authFetch(`/api/agent/session/${sessionId}/nodes`);
      const data = await res.json();
      if (data.success) {
        const payload = data.data ?? {};
        const rawNodes = normalizeRecoverablePrototypeQa((payload.nodes ?? payload) as Record<string, NodeOutput>);
        // Wanxiang/异步素材事件可能先落一个短暂 failed，再在下一轮补齐 done。
        // 失败状态延迟一个短窗口，避免工作台闪烁成失败。
        const now = Date.now();
        const nextNodes: Record<string, NodeOutput> = { ...rawNodes };
        for (const [key, next] of Object.entries(rawNodes)) {
          const previous = nodesRef.current[key];
          if (next.status === 'failed' && previous?.status === 'running') {
            const firstSeen = pendingFailureRef.current[key] ?? now;
            pendingFailureRef.current[key] = firstSeen;
            if (now - firstSeen < 1500) nextNodes[key] = previous;
          } else if (next.status === 'failed' && previous?.status === 'done') {
            // 旧的失败快照不能覆盖已经落库的完成态（常见于 websocket/轮询乱序）。
            nextNodes[key] = previous;
            delete pendingFailureRef.current[key];
          } else if (next.status === 'running' && previous?.status === 'done') {
            // 完成态是单向终态；迟到的 running 事件同样不能让 UI 倒退。
            nextNodes[key] = previous;
            delete pendingFailureRef.current[key];
          } else if (next.status !== 'failed') {
            delete pendingFailureRef.current[key];
          } else if (now - (pendingFailureRef.current[key] ?? now) >= 1500) {
            delete pendingFailureRef.current[key];
          }
        }
        const nextPipeline = (payload.pipeline ?? null) as PipelinePauseState | null;
        const snapshot = JSON.stringify(nextNodes) + '|' + JSON.stringify(nextPipeline);
        if (snapshot !== lastSnapshotRef.current) {
          lastSnapshotRef.current = snapshot;
          lastChangeRef.current = Date.now();
          setTimedOut(false);
        }
        nodesRef.current = nextNodes;
        setNodes(nextNodes);
        setNodesSessionId(sessionId);
        setPipeline(nextPipeline);
        // 微调状态轮询同步：刷新后 SSE 断流，靠此补齐 running/终态（修「微调完成不通知前端」）
        syncRefineState(payload.refine as Record<string, unknown> | null | undefined);
        // 拉完立即判断是否需要开启轮询——比等 shouldPoll dep 链快一步
        const nv = Object.values(nextNodes);
        const ad = nv.length > 0 && nv.every((n) => n.status === 'done' || n.status === 'failed');
        const ha = Object.entries(nextNodes).some(([key, n]) =>
          key !== 'requirement' && (n.status === 'running' || n.status === 'done' || n.status === 'failed'),
        );
        const refineRunning = (payload.refine as { status?: string } | null)?.status === 'running';
        // BUG-20260827-WEB-003: pipelineState 已 running 但 nodeOutputs 仍全 pending（确认后首段尚未落库）
        // 时 hasRealActivity=false → 轮询不启动，刷新后流水线冻结；须以 pipeline 态兜底开轮询。
        const pipelineActive = nextPipeline?.status === 'running' || nextPipeline?.status === 'paused';
        if ((ha && !ad) || refineRunning || pipelineActive) {
          if (timer.current) clearInterval(timer.current);
          timer.current = setInterval(() => { fetchNodes(); }, 2000);
        } else {
          if (timer.current) { clearInterval(timer.current); timer.current = undefined; }
        }
      }
    } catch { /* ignore */ }
  }, [sessionId]);

  // 初始化 + session 切换
  useEffect(() => {
    if (!sessionId) {
      prevSessionRef.current = null;
      setNodes({});
      setNodesSessionId(null);
      nodesRef.current = {};
      pendingFailureRef.current = {};
      return;
    }
    const sessionChanged = prevSessionRef.current !== sessionId;
    prevSessionRef.current = sessionId;
    if (sessionChanged) {
      setNodes({});
      setNodesSessionId(sessionId);
      nodesRef.current = {};
      setPipeline(null);
      pendingFailureRef.current = {};
      setTimedOut(false);
      lastChangeRef.current = Date.now();
      lastSnapshotRef.current = '';
    }
    setLoading(true);
    fetchNodes().finally(() => setLoading(false));
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [sessionId, fetchNodes]);

  // 外部主动刷新：拉一次数据（轮询由 fetchNodes 内部按结果自动启停）
  const refresh = useCallback(() => {
    fetchNodes();
  }, [fetchNodes]);

  // 超时检查
  useEffect(() => {
    if (!isRunning || allDone) { setTimedOut(false); return; }
    staleTimer.current = setInterval(() => {
      if (Date.now() - lastChangeRef.current > STALE_TIMEOUT) setTimedOut(true);
    }, 5000);
    return () => { if (staleTimer.current) clearInterval(staleTimer.current); };
  }, [isRunning, allDone]);

  return { nodes, nodesSessionId, pipeline, loading, allDone, timedOut, prePopulatedOnly, refresh };
}
